'use strict';

const log = false;

var _parent = {};

var error = {
  /**
   * Initializes the child module, setting up a reference to the parent SAT index
   * This allows the child module to reference all other modules without additional configuration
   *
   * @param  {object} p - The parent SAT module exported from the root index file
   */

  init: p => (_parent = p),

  /** List of strict error types */

  types: Object.freeze({
    AUTHENTICATION: 'authentication_error',
    INVALID_REQUEST: 'invalid_request_error',
    VALIDATION: 'validation_error',
    API: 'api_error',
  }),

  /** List of strict error codes */

  codes: Object.freeze({
    UNKNOWN: {
      code: 'unknown_error',
      desc: 'An unexpected error has occurred while processing the request.',
    },
    INTERNAL_SERVER_ERROR: {
      code: 'internal_server_error',
      desc: 'We cannot currently reach one of our subsystems, please try again later.',
    },
    ACCESS_DENIED: {
      code: 'access_denied',
      desc: 'Provided credentials are unauthorized.',
    },
    MISSING_SCOPE: {
      code: 'missing_scope',
      desc: 'A scope required to perform the operation is not present in the provided authentication token.',
    },
    REQUEST_INVALID: {
      code: 'request_invalid',
      desc: 'The request structure is invalid and cannot be used.',
    },
    ADDRESS_INVALID: {
      code: 'address_invalid',
      desc: 'An address provided for a location is invalid and cannot be used.',
    },
    MOVE_INVALID: {
      code: 'move_invalid',
      desc: 'The Move ID provided was invalid.',
    },
    MOVE_DUPLICATION: {
      code: 'move_duplication',
      desc: 'The move provided is a duplicate of one that is already in-progress (based on "duplicate_check" request parameter).',
    },
    NO_MOVE_FOUND: {
      code: 'no_move_found',
      desc: 'No Move found with the provided Move ID.',
    },
    FIELD_INVALID: {
      code: 'field_invalid',
      desc: 'A field in the request is invalid.',
    },
    FIELD_MISSING: {
      code: 'field_missing',
      desc: 'A field in the request is missing. This field is required and has no default fallback value.',
    },
    DUPLICATE_ADDRESS: {
      code: 'duplicate_address',
      desc: 'Origin and destination addresses cannot be the same',
    },
    OUTSIDE_REGION: {
      code: 'outside_region',
      desc: 'At least one location must be within a Hopdrive service region',
    },
  }),

  /**
   * Build an authentication error
   *
   * @param {object} errorCode - Error code from the list of codes, which contains a code and desc
   * @param {string} message - (Optional) Message string that overwrites the default message from the errorCode
   * @returns {object} A consistent error object
   */

  buildAuthenticationError: (errorCode, message) => buildTypedError(error.types.AUTHENTICATION, errorCode, message),

  /**
   * Build an invalid request error
   *
   * @param {object} errorCode - Error code from the list of codes, which contains a code and desc
   * @param {string} message - (Optional) Message string that overwrites the default message from the errorCode
   * @returns {object} A consistent error object
   */

  buildInvalidRequestError: (errorCode, message) => buildTypedError(error.types.INVALID_REQUEST, errorCode, message),

  /**
   * Build a validation error
   *
   * @param {object} errorCode - Error code from the list of codes, which contains a code and desc
   * @param {string} message - (Optional) Message string that overwrites the default message from the errorCode
   * @returns {object} A consistent error object
   */

  buildValidationError: (errorCode, message) => buildTypedError(error.types.VALIDATION, errorCode, message),

  /**
   * Build an API error
   *
   * @param {object} errorCode - Error code from the list of codes, which contains a code and desc
   * @param {string} message - (Optional) Message string that overwrites the default message from the errorCode
   * @returns {object} A consistent error object
   */

  buildApiError: (errorCode, message) => buildTypedError(error.types.API, errorCode, message),
};

module.exports = error;

const errorCodeIsValid = errorCode => {
  if (!errorCode) return false;
  if (!errorCode.code) return false;
  if (!errorCode.desc) return false;
  return true;
};

const getDocUrl = errorCode => {
  if (!errorCodeIsValid(errorCode)) throw Error('Valid errorCode object required.');
  return `https://api.hopdrive.com/docs/error-codes#${errorCode.code}`;
};

const unknownError = {
  type: error.types.API,
  code: error.codes.UNKNOWN.code,
  message: error.codes.UNKNOWN.desc,
  doc_url: getDocUrl(error.codes.UNKNOWN),
};

const buildError = options => {
  return Object.assign({}, unknownError, options);
};

const buildTypedError = (errorType, errorCode, message) => {
  if (!errorType) throw Error('Valid error type string required.');
  if (!errorCodeIsValid(errorCode)) throw Error('Valid errorCode object required.');

  let res = buildError({
    type: errorType,
    code: errorCode.code,
    message: message ? message : errorCode.desc,
    doc_url: getDocUrl(errorCode),
  });

  return res;
};
